import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Menu,
  Rating,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { isMobile } from 'react-device-detect';
import { GrLocation } from 'react-icons/gr';
import { BsChat, BsThreeDots } from 'react-icons/bs';
import { HiOutlineShare } from 'react-icons/hi';
import { FaHeart } from 'react-icons/fa';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { LiaClipboardCheckSolid } from 'react-icons/lia';
import { ReactNode } from 'react';
import {
  CandidateStage,
  type NormalizedCandidateShape,
} from '@company/types/job-overview.type';
import type { IWorkerData } from '@th-types/worker.type';
import { useChatContext } from '@company/state/chatContext';
import { useJobDetailStore } from '@company/pages/JobOverview/DetailV2/stores/jobDetailStore';
import { useHideCandidate } from '@company/hooks/jobDetailHooks/useHideCandidate';
import { useConsiderCandidate } from '@company/hooks/jobDetailHooks/useConsiderCandidate';
import { useDismissNewApplicant } from '@company/hooks/jobDetailHooks/useDismissNewApplicants';
import StrikeIconWrapper from '@components/StrikeIconWrapper/StrikeIconWrapper';

interface CandidateCardProps {
  candidate: NormalizedCandidateShape;
  jobId: number;
}

interface CardAction {
  label: string;
  icon: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}

const ACTION_ICON_SIZE = 24;

export default function CandidateCard({
  candidate,
  jobId,
}: CandidateCardProps) {
  const openWorkerModal = useJobDetailStore((state) => state.openWorkerModal);
  const openShareModal = useJobDetailStore((state) => state.openShareModal);
  const activeTab = useJobDetailStore((state) => state.activeTab);
  const { openChat } = useChatContext();
  const hideCandidate = useHideCandidate();
  const considerCandidate = useConsiderCandidate();
  const dismissNewApplicant = useDismissNewApplicant();
  const isNew = candidate.tags.some((tag) => tag.label === 'NEW');
  const isHidden =
    (candidate.hidden ?? false) || activeTab === CandidateStage.HIDDEN;
  const isConsidered = candidate.considered ?? false;
  const canConsider =
    activeTab !== CandidateStage.OFFERED && activeTab !== CandidateStage.HIRED;

  const handleMessage = () => {
    const person = {
      id: candidate.id,
      fullName: candidate.name,
      profileImageUrl: candidate.avatarUrl,
    } as IWorkerData;
    openChat(person, candidate.jobAssignmentId ?? null, false);
  };

  const handleToggleHide = () => {
    if (!candidate.jobAssignmentId) return;
    hideCandidate.mutate({
      jobAssignmentId: candidate.jobAssignmentId,
      hide: !isHidden,
    });
  };

  const handleToggleConsider = () => {
    considerCandidate.mutate({
      jobId,
      workerId: candidate.id,
      consider: !isConsidered,
    });
  };

  const handleDismissNew = () => {
    if (!candidate.jobAssignmentId) return;
    dismissNewApplicant.mutate({
      jobId,
      jobAssignmentId: candidate.jobAssignmentId,
    });
  };

  const considerAction: CardAction[] = canConsider
    ? [
        {
          label: isConsidered ? 'Unconsider' : 'Consider',
          icon: isConsidered ? (
            <StrikeIconWrapper size={ACTION_ICON_SIZE}>
              <LiaClipboardCheckSolid size={ACTION_ICON_SIZE} />
            </StrikeIconWrapper>
          ) : (
            <LiaClipboardCheckSolid size={ACTION_ICON_SIZE} />
          ),
          onClick: handleToggleConsider,
          disabled: considerCandidate.isPending,
        },
      ]
    : [];

  const actions: CardAction[] = [
    {
      label: 'Message',
      icon: <BsChat size={ACTION_ICON_SIZE} />,
      onClick: handleMessage,
    },
    ...considerAction,
    {
      label: 'Share profile',
      icon: <HiOutlineShare size={ACTION_ICON_SIZE} />,
      onClick: () => openShareModal(candidate.id),
    },
    {
      label: isHidden ? 'Unhide' : 'Hide',
      icon: isHidden ? (
        <FiEye size={ACTION_ICON_SIZE} />
      ) : (
        <FiEyeOff size={ACTION_ICON_SIZE} />
      ),
      onClick: handleToggleHide,
      disabled: !candidate.jobAssignmentId || hideCandidate.isPending,
    },
  ];

  const desktopActions = (
    <Group gap="sm" wrap="nowrap" onClick={(e) => e.stopPropagation()}>
      {actions.map((action) => (
        <Tooltip key={action.label} label={action.label} withArrow>
          <ActionIcon
            variant="subtle"
            color="dark"
            aria-label={action.label}
            onClick={action.onClick}
            disabled={action.disabled}
          >
            {action.icon}
          </ActionIcon>
        </Tooltip>
      ))}
    </Group>
  );

  const mobileActions = (
    <Box onClick={(e) => e.stopPropagation()}>
      <Menu position="bottom-end" shadow="md" width={180}>
        <Menu.Target>
          <ActionIcon variant="subtle" color="dark" aria-label="Actions">
            <BsThreeDots size={20} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          {actions.map((action) => (
            <Menu.Item
              key={action.label}
              leftSection={action.icon}
              onClick={action.onClick}
              disabled={action.disabled}
            >
              {action.label}
            </Menu.Item>
          ))}
          {isNew && (
            <>
              <Menu.Divider />
              <Menu.Item
                onClick={handleDismissNew}
                disabled={
                  !candidate.jobAssignmentId || dismissNewApplicant.isPending
                }
              >
                Dismiss New
              </Menu.Item>
            </>
          )}
        </Menu.Dropdown>
      </Menu>
    </Box>
  );

  const tagBadges = candidate.tags.map((tag) => (
    <Badge key={tag.label} variant={tag.variant} size="sm">
      {tag.label}
    </Badge>
  ));

  return (
    <Card
      withBorder
      radius="md"
      padding="sm"
      style={{ boxShadow: 'none', cursor: 'pointer', flexShrink: 0 }}
      onClick={() => openWorkerModal(candidate.id)}
    >
      <Group align="flex-start" wrap="nowrap" gap={isMobile ? 12 : 20}>
        <Avatar
          src={candidate.avatarUrl}
          name={candidate.name}
          color="initials"
          radius="sm"
          size={isMobile ? 56 : 86}
        />

        <Stack gap={4} flex={1}>
          <Group justify="space-between" wrap="nowrap" align="flex-start">
            <Group gap={6}>
              <Text fw={700}>{candidate.name}</Text>
              {candidate.favorite && <FaHeart color="green" size={12} />}
              {candidate.inNetwork && (
                <Badge color="brandYellow" size="sm">
                  IN-NETWORK
                </Badge>
              )}
            </Group>

            {isMobile ? mobileActions : desktopActions}
          </Group>

          <Group gap="xs" wrap="nowrap">
            {/* TODO: remove conditional when BE returns missing fields */}
            {candidate.miles != null && (
              <>
                <GrLocation />
                <Text size="sm">{candidate.miles.toFixed(1)} miles</Text>
              </>
            )}
            <Rating value={candidate.rating} readOnly fractions={2} size="sm" />
          </Group>

          {/* Desktop: tags + dismiss stay inside the data column. */}
          {!isMobile && (
            <Group justify="space-between" align="flex-end">
              <Group gap="xs">{tagBadges}</Group>
              {isNew && (
                <Button
                  variant="default"
                  loading={dismissNewApplicant.isPending}
                  disabled={!candidate.jobAssignmentId}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDismissNew();
                  }}
                >
                  Dismiss New
                </Button>
              )}
            </Group>
          )}
        </Stack>
      </Group>

      {/* Mobile: tags become a separate full-width row below the data. */}
      {isMobile && (
        <Group gap="xs" mt="sm">
          {tagBadges}
        </Group>
      )}
    </Card>
  );
}
